import { getAuth } from 'firebase/auth';

interface UserUpdate {
  first_name?: string;
  last_name?: string;
  email?: string;
  organization?: string;
  password?: string;
  current_password?: string;
  access_level?: number;
}

interface DocumentResponse {
  id: number;
  filename: string;
  file_path: string;
  file_size: number;
  content_type: string;
  created_at: string;
  updated_at: string;
  user_id: number;
}

interface DocumentList {
  documents: DocumentResponse[];
  total: number;
}

interface ChatbotCreate {
  bot_name: string;
  default_prompt?: string; // Optional: if you have a default prompt
  status?: string;         // Optional: if you manage status like 'active', 'inactive'
  // Add any other fields that your `schemas.ChatbotCreate` expects
}

interface ChatbotResponse {
  id: string;
  bot_name: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  status: string;
  default_prompt: string | null;
  // Add any other fields that your `schemas.ChatbotResponse` returns
}

interface ChatbotList {
  chatbots: ChatbotResponse[];
  total: number;
}

interface ConfigurationFile {
  id: string;
  original_filename: string;
  filename?: string; // For backward compatibility
  gcs_uri: string;
  file_size: number;
  content_type: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ConfigurationContentResponse {
  content: string;
  filename: string;
}

// API base URL - configured to use development or production URL
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://clai-backend-273699837303.us-central1.run.app' // Replace with your production URL when deployed
  : 'http://localhost:8000';

// Helper function for handling API errors
const handleApiError = async (response: Response) => {
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      // If the backend is unreachable or returns no JSON, show a friendly message
      throw new Error(
        'Our service is temporarily unavailable. This is not an issue with your connection. We are working to restore service as soon as possible. Please try again later.'
      );
    }
    throw new Error(errorData.detail || 'API request failed');
  }
  return response.json();
};

// Helper to get Firebase ID token
const getFirebaseIdToken = async (): Promise<string | null> => {
  let auth;
  try {
    auth = getAuth();
  } catch (e) {
    // Firebase not initialized
    return null;
  }
  const user = auth?.currentUser;
  if (user && typeof user.getIdToken === 'function') {
    return await user.getIdToken();
  }
  return null;
};

// API Service with authentication methods
const ApiService = {
  // Get current user info (requires Firebase ID token)
  getCurrentUser: async (): Promise<any> => {
    const idToken = await getFirebaseIdToken();
    if (!idToken) throw new Error('Not authenticated');
    const response = await fetch(`${API_BASE_URL}/users/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
    return handleApiError(response);
  },

  // Update user info (requires Firebase ID token)
  updateUser: async (userData: UserUpdate): Promise<any> => {
    const idToken = await getFirebaseIdToken();
    if (!idToken) throw new Error('Not authenticated');
    const response = await fetch(`${API_BASE_URL}/users/me`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify(userData),
    });
    return handleApiError(response);
  },
  
  // Upload document (text file)
  uploadDocument: async (file: File): Promise<DocumentResponse> => {
    const idToken = await getFirebaseIdToken();
    if (!idToken) throw new Error('Not authenticated');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API_BASE_URL}/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        body: formData,
      });
      if (response.status === 401) {
        throw new Error('Authentication failed: Please log in again');
      }
      return handleApiError(response);
    } catch (error) {
      throw error;
    }
  },
  
  // Get all documents for the current user
  getUserDocuments: async (skip: number = 0, limit: number = 100): Promise<DocumentList> => {
    const idToken = await getFirebaseIdToken();
    if (!idToken) throw new Error('Not authenticated');
    try {
      const response = await fetch(`${API_BASE_URL}/documents/?skip=${skip}&limit=${limit}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });
      if (response.status === 401) {
        throw new Error('Authentication failed: Please log in again');
      }
      return handleApiError(response);
    } catch (error) {
      throw error;
    }
  },
  
  // Get a specific document by ID
  getDocument: async (documentId: number): Promise<DocumentResponse> => {
    const idToken = await getFirebaseIdToken();
    if (!idToken) throw new Error('Not authenticated');
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${documentId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });
      if (response.status === 401) {
        throw new Error('Authentication failed: Please log in again');
      }
      return handleApiError(response);
    } catch (error) {
      throw error;
    }
  },
  
  // Get download URL for a document
  getDocumentDownloadUrl: (documentId: number): string => {
    return `${API_BASE_URL}/documents/${documentId}/download`;
  },

  // Create a new chatbot
  createChatbot: async (chatbotData: ChatbotCreate): Promise<ChatbotResponse> => {
    const idToken = await getFirebaseIdToken();
    if (!idToken) throw new Error('Not authenticated');
    const response = await fetch(`${API_BASE_URL}/chatbots/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify(chatbotData),
    });
    return handleApiError(response);
  },

  // List chatbots for the current user
  listChatbots: async (skip: number = 0, limit: number = 100): Promise<ChatbotList> => {
    const idToken = await getFirebaseIdToken();
    if (!idToken) throw new Error('Not authenticated');
    const response = await fetch(`${API_BASE_URL}/chatbots/?skip=${skip}&limit=${limit}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
    return handleApiError(response);
  },

  // Delete a chatbot by ID
  deleteChatbot: async (chatbotId: string): Promise<void> => {
    const idToken = await getFirebaseIdToken();
    if (!idToken) throw new Error('Not authenticated');
    const response = await fetch(`${API_BASE_URL}/chatbots/${chatbotId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
    // For 204 No Content, we don't expect JSON, so handle differently
    if (!response.ok) {
      await handleApiError(response); // This will throw if there's an error JSON
    }
    // If response.ok and status is 204, it's a success with no content
    return; // Explicitly return nothing for void
  },

  // Configuration management methods
  uploadConfiguration: async (chatbotId: string, formData: FormData, configId?: string): Promise<any> => {
    const idToken = await getFirebaseIdToken();
    if (!idToken) throw new Error('Not authenticated');
    
    // Build URL with optional config_id parameter
    let url = `${API_BASE_URL}/chatbots/${chatbotId}/config/upload`;
    if (configId) {
      url += `?config_id=${encodeURIComponent(configId)}`;
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: formData,
    });
    return handleApiError(response);
  },

  listConfigurations: async (chatbotId: string): Promise<ConfigurationFile[]> => {
    const idToken = await getFirebaseIdToken();
    if (!idToken) throw new Error('Not authenticated');
    const response = await fetch(`${API_BASE_URL}/chatbots/${chatbotId}/config/list`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
    return handleApiError(response);
  },

  downloadConfiguration: async (chatbotId: string, configName: string): Promise<any> => {
    const idToken = await getFirebaseIdToken();
    if (!idToken) throw new Error('Not authenticated');
    const response = await fetch(`${API_BASE_URL}/${chatbotId}/config/download/${encodeURIComponent(configName)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
    return handleApiError(response);
  },

  loadConfigurationContent: async (chatbotId: string, configId: string): Promise<ConfigurationContentResponse> => {
    const idToken = await getFirebaseIdToken();
    if (!idToken) throw new Error('Not authenticated');
    const response = await fetch(`${API_BASE_URL}/chatbots/${chatbotId}/config/load/${configId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });
    return handleApiError(response);
  },
};

export default ApiService;